import { Component } from '@theme/component';
import { prefersReducedMotion, preloadImage, lockScroll, unlockScroll } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { getScrollTop, scrollTo } from '@theme/scroll-container';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element - also the real scroll/clipping viewport on both mobile and desktop (see this file's own CSS), not just the modal boundary.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {IntersectionObserver | null} */
  #activeObserver = null;

  /**
   * Owner request (2026-08-27): IntersectionObserver callbacks only ever
   * report entries whose own threshold crossing changed since the last
   * check - not a fresh snapshot of every observed target's current
   * state (confirmed live: clicking straight to image 5 left image 6
   * active instead, because image 6's own "just started intersecting"
   * event fired as a lone one-entry batch while image 5 - still
   * intersecting from before, unchanged - never re-appeared in that
   * batch to be compared against). Tracking every target's last-known
   * overlap here and resolving the winner from the full map (not just
   * the latest batch) on every callback fixes that.
   * @type {Map<Element, number>}
   */
  #intersectionHeights = new Map();

  /**
   * Owner request (2026-08-24), mobile tap-to-zoom: the page behind this
   * dialog was still scrollable while it was open (no lockScroll call
   * anywhere in this file), and closing never restored where the page
   * had been scrolled to before opening - both confirmed live. Matches
   * the same lockScroll/unlockScroll + save-scrollY-on-open/restore-on-
   * close pattern assets/dialog.js's DialogComponent (the theme's other
   * native-<dialog> component) already uses, rather than inventing a
   * second approach.
   * @type {number}
   */
  #previousScrollY = 0;

  /** @type {ResizeObserver | null} */
  #indexResizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setupActiveObserver();
    this.#setupIndexResizeObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeObserver?.disconnect();
    this.#indexResizeObserver?.disconnect();
    unlockScroll(this.refs.dialog);
  }

  /**
   * Owner request (2026-08-30): "recalculate only on viewer open/resize/
   * media-count change; no scroll-time calculations" - a ResizeObserver
   * on the dialog itself covers resize (its own size tracks the
   * viewport, see this file's own CSS) without a global window listener
   * to clean up separately, and fires once on its own initial observe()
   * too. open() below also calls #recalculateIndexSpacing() directly,
   * synchronously, right after showModal() - the observer's first
   * callback is asynchronous, and relying on it alone would leave the
   * dialog briefly relying on the unadjusted CSS default before it
   * fires. Media count itself doesn't change after this component
   * connects (the index is rendered server-side from the product's
   * fixed media list), so it doesn't need its own trigger - open()'s
   * explicit call covers "the state that matters when the list is
   * first shown."
   */
  #setupIndexResizeObserver() {
    const { dialog } = this.refs;
    if (!dialog) return;

    this.#indexResizeObserver = new ResizeObserver(() => this.#recalculateIndexSpacing());
    this.#indexResizeObserver.observe(dialog);
  }

  /**
   * Owner request (2026-08-30): the numeric index's preferred (large)
   * vertical spacing (product-media-gallery-content.liquid's own 36px-
   * per-side default, --index-gap's fallback) can make the full list
   * taller than the viewer itself once there are enough images -
   * confirmed live at 8 images on a short mobile viewport, where the
   * list's own padded boxes ran past both edges. This computes the
   * largest padding that still lets every number's own text fit within
   * the viewer's height, without ever shrinking font-size or
   * introducing scroll/clipping - text height is backed out of the
   * CURRENTLY rendered item (itemHeight - currentPadding*2), which
   * stays correct however many times this runs, whatever padding is
   * already applied, since that's solving for the same font-driven
   * constant every time, not accumulating.
   */
  #recalculateIndexSpacing() {
    const { dialog, thumbnails } = this.refs;
    if (!thumbnails) return;

    const items = /** @type {HTMLElement[]} */ (Array.from(thumbnails.children));
    if (!items.length) return;

    const firstItem = items[0];
    const style = getComputedStyle(firstItem);
    const currentGap = parseFloat(style.paddingTop) || 0;
    const textHeight = firstItem.getBoundingClientRect().height - currentGap * 2;

    // Must match the CSS's own --index-gap fallback (product-media-
    // gallery-content.liquid) - the preferred/ceiling spacing to use
    // whenever the full list already fits without shrinking it.
    const preferredGap = 36;
    const availableHeight = dialog.clientHeight || window.innerHeight;
    const preferredTotal = items.length * (textHeight + preferredGap * 2);

    const gap =
      preferredTotal <= availableHeight ? preferredGap : Math.max(0, (availableHeight / items.length - textHeight) / 2);

    thumbnails.style.setProperty('--index-gap', `${gap}px`);
  }

  /**
   * Owner request (2026-08-27): tracks which media item is "active" via a
   * thin horizontal band centered in the dialog itself - the dialog is
   * the real scroll container on both mobile and desktop (see this
   * file's own CSS) - instead of a 'scroll' event listener, which was
   * explicitly ruled out. Whichever media item overlaps that center band
   * the most (by absolute overlap height, not intersectionRatio - a tall
   * image only lightly grazing the band would otherwise lose to a short
   * image mostly inside it, purely because the short one's ratio is
   * higher) becomes active. Because the band is narrow, only one item is
   * ever the clear winner at a given scroll position, so the active
   * number can't flicker ambiguously between two adjacent images the way
   * "most visible overall" could.
   */
  #setupActiveObserver() {
    const { dialog, media } = this.refs;
    if (!dialog || !media.length) return;

    this.#activeObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#intersectionHeights.set(entry.target, entry.isIntersecting ? entry.intersectionRect.height : 0);
        }

        /** @type {Element | null} */
        let bestTarget = null;
        let bestHeight = 0;
        for (const [target, height] of this.#intersectionHeights) {
          if (height > bestHeight) {
            bestHeight = height;
            bestTarget = target;
          }
        }
        if (!bestTarget) return;

        const index = media.indexOf(/** @type {HTMLElement} */ (bestTarget));
        if (index === -1) return;

        this.#setActiveMedia(index);
        this.loadHighResolutionImage(/** @type {HTMLElement} */ (bestTarget));
        this.dispatchEvent(new ZoomMediaSelectedEvent(index));
      },
      {
        root: dialog,
        rootMargin: '-40% 0px -40% 0px',
        threshold: 0,
      }
    );

    for (const item of media) this.#activeObserver.observe(item);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    this.#previousScrollY = getScrollTop();
    lockScroll(dialog);
    dialog.showModal();
    this.#recalculateIndexSpacing();
    this.#setActiveMedia(index);

    for (const target of [targetThumbnail, targetImage]) {
      target?.scrollIntoView({ behavior: 'instant' });
    }

    // Owner request (2026-08-27): scrollIntoView() above targets elements
    // inside this dialog (needed on mobile to position its own internal
    // scroll at image N) - but on desktop (>=990px) `.page-wrapper` is the
    // real page scroll container and also the containing block for this
    // fixed-position dialog (confirmed live: opening the dialog dragged
    // page-wrapper.scrollTop from 500 to 1886), so scrollIntoView drags the
    // page's own scroll along with it as a side effect it doesn't know
    // about. Re-applied right after, using the same utility closeDialog()
    // already uses - it's a different scrollable element than the
    // dialog's internal one on mobile, so this doesn't undo the "open at
    // image N" positioning scrollIntoView just did.
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Owner request (2026-08-24): marks one media item and its
   * corresponding index button as active (aria-selected="true"), all
   * others false - shared by the center-band observer and
   * selectThumbnail so both stay in sync, exactly like before this
   * method existed. The only
   * change is now also setting it on the media item itself, not just
   * the thumbnail/index button - the desktop viewer (snippets/product-
   * media-gallery-content.liquid) shows only the aria-selected media
   * item and hides the rest, which needs that state to exist somewhere
   * more durable than "whichever element IntersectionObserver most
   * recently reported," and media items never carried it before.
   * @param {number} index
   */
  #setActiveMedia(index) {
    const { media, thumbnails } = this.refs;

    thumbnails?.querySelectorAll('button').forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    media.forEach((item, i) => {
      item.setAttribute('aria-selected', `${i === index}`);
    });
  }

  /**
   * Closes the zoom dialog.
   *
   * Owner request (2026-08-28): this used to run a view-transition-based
   * close (shared-element morph from the dialog's active image back to
   * the underlying gallery slide), matching the shared-element morph
   * open() used to have. Confirmed live (rapid-fire screenshots through
   * the close click) that it's the same class of bug already root-
   * caused and fixed on the open() side: the transition assumes exactly
   * one image is visible in both the old and new states, but this
   * dialog's own continuous-scroll layout keeps every media item's real
   * DOM content on screen throughout - so for the transition's whole
   * duration, the real (about-to-be-restored) underlying page and the
   * real (still-open) dialog content are simultaneously visible and
   * misaligned, which is exactly what read as "close flickers" and "the
   * image doubles." Closing is now a single synchronous state change,
   * same as opening already is - nothing to un-animate, nothing for two
   * states to overlap during.
   */
  close() {
    this.closeDialog();
  }

  closeDialog() {
    const { dialog } = this.refs;
    unlockScroll(dialog);
    dialog.close();
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#setActiveMedia(index);

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
